import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { Badge, Button, Input, LoadingSpinner } from '@/components/common';
import { useUpdateUser, useUser } from '@/hooks/useUsers';
import { getErrorMessage, getFieldErrors, NON_FIELD_KEY } from '@/services/api';
import { userStatusOf } from '@/services/users';
import { UserAccountActions } from '@/pages/users/UserAccountActions';
import { USER_STATUS_BADGE_VARIANT, USER_STATUS_LABELS } from '@/pages/users/useSetUserStatus';

type AdminFields = { first_name: string; last_name: string; email: string; phone: string };
const EDITABLE: (keyof AdminFields)[] = ['first_name', 'last_name', 'email', 'phone'];

/**
 * The broker admin's LOGIN, managed in place on Broker Detail > Broker Admin.
 *
 * Country admins used to be sent to `/users/<id>` (Manage user) or
 * `/users/<id>/edit` (Edit admin) to do this. `/users` is not routed for a
 * country admin, so every way back out ended on Access Denied (B-05); and the
 * status / reset actions lived only on that page (M-06). Both now happen here.
 *
 * Deliberately NO role field. `role` is read-only on `PATCH /users/{id}/`, so
 * a role picker here would be a control that saves nothing.
 *
 * The record is fetched through `GET /users/{id}/`, the same scoped endpoint
 * every write below goes through, so what is shown is exactly what the
 * country admin is allowed to act on -- and a load failure is said as one,
 * rather than rendered as an account with no details.
 */
export function BrokerAdminAccount({ adminId, brokerId }: { adminId: string; brokerId: string }) {
  const queryClient = useQueryClient();
  const { data: admin, isLoading, isError, error, refetch } = useUser(adminId);
  const updateMutation = useUpdateUser();

  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<AdminFields>({ first_name: '', last_name: '', email: '', phone: '' });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (admin && !editing) {
      setValues({
        first_name: admin.first_name || '',
        last_name: admin.last_name || '',
        email: admin.email || '',
        phone: admin.phone || '',
      });
    }
  }, [admin, editing]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError || !admin) {
    return (
      <div
        role="alert"
        className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-400"
      >
        <p className="font-semibold">The admin&apos;s account could not be loaded.</p>
        <p className="mt-1">{getErrorMessage(error)}</p>
        <button type="button" onClick={() => refetch()} className="mt-2 underline">
          Try again
        </button>
      </div>
    );
  }

  const status = userStatusOf(admin);

  const startEditing = () => {
    setFieldErrors({});
    setEditing(true);
  };

  const cancel = () => {
    setFieldErrors({});
    setEditing(false);
  };

  const save = (event: React.FormEvent) => {
    event.preventDefault();
    const email = values.email.trim();
    const phone = values.phone.trim();
    // The server's own rule for a login: it needs an email or a mobile. Said
    // here rather than letting "" reach a unique column.
    if (!email && !phone) {
      setFieldErrors({ email: 'Enter an email or a mobile number — the admin signs in with one of them.' });
      return;
    }
    setFieldErrors({});
    updateMutation.mutate(
      {
        id: adminId,
        data: {
          first_name: values.first_name.trim(),
          last_name: values.last_name.trim(),
          // null, not "": both columns are unique and nullable, and a second
          // blank would collide.
          email: email || null,
          phone: phone || null,
        },
      },
      {
        onSuccess: () => {
          // The broker record reads its admin_* fields through to this login.
          queryClient.invalidateQueries({ queryKey: ['broker', brokerId] });
          setEditing(false);
        },
        onError: (err) => {
          const parsed = getFieldErrors(err);
          setFieldErrors(Object.keys(parsed).length ? parsed : { [NON_FIELD_KEY]: getErrorMessage(err) });
        },
      }
    );
  };

  const set = (field: keyof AdminFields) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setValues((prev) => ({ ...prev, [field]: event.target.value }));

  // Anything the server rejected that is not one of the four inputs (a
  // non-field error, or a field this form does not show) still has to reach
  // the screen -- otherwise the save looks like it did nothing.
  const otherErrors = Object.entries(fieldErrors).filter(
    ([key]) => !EDITABLE.includes(key as keyof AdminFields)
  );

  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      {editing ? (
        <form onSubmit={save} noValidate className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              id="broker-admin-first-name"
              label="First name"
              value={values.first_name}
              onChange={set('first_name')}
              error={fieldErrors.first_name}
            />
            <Input
              id="broker-admin-last-name"
              label="Last name"
              value={values.last_name}
              onChange={set('last_name')}
              error={fieldErrors.last_name}
            />
            <Input
              id="broker-admin-email"
              type="email"
              label="Email"
              value={values.email}
              onChange={set('email')}
              error={fieldErrors.email}
            />
            <Input
              id="broker-admin-phone"
              type="tel"
              label="Mobile"
              value={values.phone}
              onChange={set('phone')}
              error={fieldErrors.phone}
            />
          </div>
          {otherErrors.length > 0 && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-400"
            >
              {otherErrors.map(([key, message]) => (
                <p key={key}>{message}</p>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            The role cannot be changed here: a broker admin login stays a broker admin.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={cancel} disabled={updateMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" size="sm" isLoading={updateMutation.isPending}>
              Save
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{admin.full_name || 'Unnamed'}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{admin.email || 'No email'}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{admin.phone || 'No mobile'}</p>
            </div>
            <div className="flex items-center gap-2">
              {status === null ? (
                <Badge variant={admin.is_active ? 'success' : 'default'}>
                  {admin.is_active ? 'Active' : 'Inactive'}
                </Badge>
              ) : (
                <Badge variant={USER_STATUS_BADGE_VARIANT[status]}>
                  {admin.status_display || USER_STATUS_LABELS[status]}
                </Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                leftIcon={<PencilSquareIcon className="w-4 h-4" />}
                onClick={startEditing}
              >
                Edit admin
              </Button>
            </div>
          </div>
          {status !== null && status !== 'active' && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This admin cannot log in, so nobody can run this broker until they are reactivated.
            </p>
          )}
          <UserAccountActions
            userId={adminId}
            userName={admin.full_name || admin.email || 'This admin'}
            currentStatus={status}
            layout="inline"
          />
        </div>
      )}
    </div>
  );
}
